/**
 * The calendar: what happened, and what's coming up, on the same grid.
 *
 * Both halves together is the whole point. A calendar of only the plan is a
 * list of intentions; a calendar of only sessions is the heatmap that already
 * exists on Progress. Seeing them against each other is what tells you you've
 * missed two Mondays in a row.
 *
 * A month grid rather than a list because the question people ask it is
 * spatial — "what does this week look like", "am I training Thursday" — and a
 * list of dates answers that badly.
 */

import { useMemo, useState } from 'react'

import { ApiError } from '../api/client'

export type ScheduleRow = {
  id: number
  plan_id: number
  plan_day_id: number
  plan_day_name: string | null
  plan_name: string | null
  scheduled_date: string
  status: 'planned' | 'completed' | 'skipped'
  workout_session_id: string | null
}

export type SessionRow = {
  id: string
  title: string | null
  date: string | null
  finished_at: string | null
  plan_id: number | null
  set_count: number
}

const WEEKDAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

/**
 * What a reserved day is called.
 *
 * Exported because the name is the identity: it's what the square says, what
 * the session is titled when you start it, and how the calendar recognises a
 * day as already reserved. Two spellings of it would mean a day you could
 * reserve twice.
 */
export const RESERVED_TITLE = 'Planned Session'

type DayState = 'done' | 'planned' | 'missed' | 'none'

type DayEntry = { planned: ScheduleRow[]; sessions: SessionRow[] }

/**
 * One colour per day, and the order of these checks is the whole design.
 *
 * Trained wins over everything. A day you worked out is green even if what you
 * did wasn't what was written — you showed up, and a square that scolded you
 * for improvising would be read once and then ignored. The day detail still
 * says the planned session went unfilled, so the nuance is available without
 * the grid nagging about it.
 *
 * Missed only applies to a slot the server has already settled as skipped,
 * which it does once the day has passed. Today never goes red while there are
 * still hours left to train.
 */
function dayState(entry: DayEntry | undefined): DayState {
  if (!entry) return 'none'
  if (entry.sessions.length > 0) return 'done'
  if (entry.planned.some((p) => p.status === 'completed')) return 'done'
  if (entry.planned.some((p) => p.status === 'planned')) return 'planned'
  if (entry.planned.some((p) => p.status === 'skipped')) return 'missed'
  return 'none'
}

const FILLS: Record<DayState, { bg: string; fg: string } | null> = {
  done: { bg: '--cal-done', fg: '--cal-done-text' },
  planned: { bg: '--cal-planned', fg: '--cal-planned-text' },
  missed: { bg: '--cal-missed', fg: '--cal-missed-text' },
  none: null,
}

// Colour alone can't carry the meaning — roughly one man in twelve wouldn't
// separate the orange from the green. These label the squares for screen
// readers and feed the legend under the grid.
const STATE_LABELS: Record<DayState, string> = {
  done: 'trained',
  planned: 'planned',
  missed: 'missed',
  none: '',
}

/** Local-time YYYY-MM-DD. `toISOString()` would silently shift the day. */
function isoDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/**
 * "Wed, Aug 12". Noon, not midnight: `new Date('2026-08-12')` is parsed as UTC
 * and renders as the 11th anywhere west of Greenwich.
 */
function formatDay(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString(
    [], { weekday: 'short', month: 'short', day: 'numeric' })
}

/** Monday-first offset for a JS day number (0 = Sunday). */
const mondayIndex = (jsDay: number) => (jsDay + 6) % 7

export function Calendar({
  schedule,
  sessions,
  today,
  onLogDay,
  onPlanDay,
  onRemovePlanned,
  onMovePlanned,
  onRestorePlanned,
  onReserveDay,
}: {
  schedule: ScheduleRow[]
  sessions: SessionRow[]
  today: string
  /**
   * Open a workout for a day. `row` is the slot it fills, or null for one that
   * wasn't planned. Past dates are backdated rather than refused — the whole
   * point is recording the session you did without your phone.
   */
  onLogDay?: (date: string, row: ScheduleRow | null) => void
  /** Write a custom workout onto a future date without starting it. */
  onPlanDay?: (date: string) => void
  /**
   * Editing a single planned slot. All three reject rather than resolve on
   * failure, so the calendar can say what went wrong instead of quietly
   * showing stale rows.
   */
  onRemovePlanned?: (row: ScheduleRow) => Promise<void>
  onMovePlanned?: (row: ScheduleRow, date: string) => Promise<void>
  onRestorePlanned?: (row: ScheduleRow) => Promise<void>
  /** Spoken for, nothing decided: one tap, no form. See RESERVED_TITLE. */
  onReserveDay?: (date: string) => Promise<void>
}) {
  const [monthOffset, setMonthOffset] = useState(0)
  const [selected, setSelected] = useState<string | null>(today)

  // The slot being moved, if any. While this is set the grid stops being a
  // date picker and becomes a destination picker — the same squares, one
  // gesture, no second calendar in a dialog on a 6-inch screen.
  const [moving, setMoving] = useState<ScheduleRow | null>(null)
  // What was last removed, kept so it can be put back. Removal is one tap and
  // nothing else in the app undoes anything, so without this a mis-tap means
  // re-running the scheduler.
  const [removed, setRemoved] = useState<ScheduleRow | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** Every edit is the same shape: block input, do it, keep or show the error. */
  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      return true
    } catch (err) {
      // Only the server's own detail is worth showing — it's written for a
      // person ("that day has passed"). A network failure arrives as the
      // browser's "Failed to fetch", which explains nothing and looks like a
      // crash. Scheduling is server-side, so offline is the likely cause.
      setError(err instanceof ApiError
        ? err.message
        : "Couldn't save that change — scheduling needs a connection.")
      return false
    } finally {
      setBusy(false)
    }
  }

  const cursor = useMemo(() => {
    const base = new Date(`${today}T12:00:00`)
    return new Date(base.getFullYear(), base.getMonth() + monthOffset, 1)
  }, [today, monthOffset])

  const byDate = useMemo(() => {
    const map = new Map<string, { planned: ScheduleRow[]; sessions: SessionRow[] }>()
    const bucket = (d: string) => {
      if (!map.has(d)) map.set(d, { planned: [], sessions: [] })
      return map.get(d)!
    }
    for (const row of schedule) bucket(row.scheduled_date).planned.push(row)
    for (const s of sessions) if (s.date) bucket(s.date).sessions.push(s)
    return map
  }, [schedule, sessions])

  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate()
    const lead = mondayIndex(first.getDay())

    const out: (string | null)[] = Array(lead).fill(null)
    for (let day = 1; day <= daysInMonth; day++) {
      out.push(isoDate(new Date(cursor.getFullYear(), cursor.getMonth(), day)))
    }
    // Pad to whole weeks so the grid doesn't reflow as months change length.
    while (out.length % 7 !== 0) out.push(null)
    return out
  }, [cursor])

  const monthLabel = cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
  const detail = selected ? byDate.get(selected) : undefined
  const isReserved = !!detail?.planned.some(
    (p) => p.status === 'planned' && p.plan_day_name === RESERVED_TITLE)

  return (
    <div className="card">
      <div className="row" style={{ marginBottom: '.5rem' }}>
        <button
          className="secondary"
          aria-label="Previous month"
          style={navStyle}
          onClick={() => setMonthOffset((m) => m - 1)}
        >
          ‹
        </button>
        <strong style={{ fontSize: '.95rem' }}>{monthLabel}</strong>
        <button
          className="secondary"
          aria-label="Next month"
          style={navStyle}
          onClick={() => setMonthOffset((m) => m + 1)}
        >
          ›
        </button>
      </div>

      {/* 4px rather than 2: today's ring is drawn 2px outside its square, and
          at a 2px gap it would butt straight into the neighbouring days. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
        {WEEKDAY_LABELS.map((label, i) => (
          <div
            key={i}
            className="muted"
            style={{ textAlign: 'center', fontSize: '.7rem', paddingBottom: '.2rem' }}
          >
            {label}
          </div>
        ))}

        {cells.map((date, i) => {
          if (date === null) return <div key={`pad${i}`} />

          const state = dayState(byDate.get(date))
          const fill = FILLS[state]
          const isToday = date === today
          const isSelected = date === selected
          // A move can only land on today or later — the server refuses the
          // past, and an enabled square that always errors is worse than a
          // dim one.
          const blocked = moving !== null && date < today

          return (
            <button
              key={date}
              className="grid-cell"
              disabled={blocked || busy}
              onClick={() => {
                if (!moving) {
                  setSelected(date)
                  return
                }
                const row = moving
                void run(async () => {
                  await onMovePlanned!(row, date)
                  setMoving(null)
                  setSelected(date)
                })
              }}
              aria-label={
                moving
                  ? `Move ${moving.plan_day_name ?? 'workout'} to ${date}`
                  : `${date}${isToday ? ', today' : ''}${
                      state === 'none' ? '' : `, ${STATE_LABELS[state]}`
                    }${isSelected ? ', selected' : ''}`
              }
              aria-current={isToday ? 'date' : undefined}
              aria-pressed={moving ? undefined : isSelected}
              style={{
                opacity: blocked ? 0.35 : 1,
                marginTop: 0,
                minHeight: 0,
                padding: 0,
                // Square cells. A month of rectangles that change height with
                // the row count reads as a table; squares read as a calendar.
                aspectRatio: '1 / 1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '.82rem',
                fontWeight: isToday ? 700 : 400,
                background: fill ? `var(${fill.bg})` : 'transparent',
                color: fill ? `var(${fill.fg})` : 'inherit',
                // Two marks that have to stay tellable apart, so they differ in
                // colour and in where they sit.
                //
                // Selection is the inner outline. It lands on top of the fill,
                // so it borrows that fill's own foreground — the one colour
                // already guaranteed to read against it.
                //
                // Today is the violet ring outside the square. Outside is what
                // makes a fixed colour safe: it sits on the page, never on a
                // green or orange or red fill, so it needs to survive exactly
                // one background. A day can be today, selected and coloured at
                // once and all three still read.
                border: isSelected
                  ? `2px solid ${fill ? `var(${fill.fg})` : 'var(--text)'}`
                  : '2px solid transparent',
                boxShadow: isToday ? '0 0 0 2px var(--cal-today)' : 'none',
                borderRadius: 'var(--radius)',
              }}
            >
              {Number(date.slice(-2))}
            </button>
          )
        })}
      </div>

      <div style={{ display: 'flex', gap: '.7rem', marginTop: '.5rem', flexWrap: 'wrap' }}>
        {(['done', 'planned', 'missed'] as const).map((state) => (
          <span key={state} style={{ display: 'flex', alignItems: 'center', gap: '.3rem' }}>
            <span
              aria-hidden
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '3px',
                background: `var(${FILLS[state]!.bg})`,
                display: 'inline-block',
              }}
            />
            <span className="muted" style={{ fontSize: '.75rem' }}>
              {STATE_LABELS[state]}
            </span>
          </span>
        ))}

        {/* The ring is a different kind of mark from the three fills, so it is
            shown as a ring and not as a fourth swatch. */}
        <span style={{ display: 'flex', alignItems: 'center', gap: '.3rem' }}>
          <span
            aria-hidden
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '3px',
              background: 'transparent',
              boxShadow: '0 0 0 2px var(--cal-today)',
              display: 'inline-block',
              marginLeft: '2px',
            }}
          />
          <span className="muted" style={{ fontSize: '.75rem' }}>today</span>
        </span>
      </div>

      <div style={{ marginTop: '.6rem', borderTop: '1px solid var(--border)', paddingTop: '.5rem' }}>
        {/* Move mode takes the whole detail panel over. Half a panel of normal
            actions beside a "tap a day" instruction reads as though both are
            live, and the next tap then means something you didn't intend. */}
        {moving ? (
          <div className="row" style={{ fontSize: '.85rem' }}>
            <span>
              Moving <strong>{moving.plan_day_name ?? 'workout'}</strong>
              <div className="muted" style={{ fontSize: '.78rem' }}>
                Tap the day it should go to.
              </div>
            </span>
            <button
              className="secondary"
              style={smallButton}
              disabled={busy}
              onClick={() => { setMoving(null); setError(null) }}
            >
              Cancel
            </button>
          </div>
        ) : !selected ? (
          <p className="muted" style={{ margin: 0, fontSize: '.85rem' }}>Pick a day.</p>
        ) : (
          <>
            {!detail?.planned.length && !detail?.sessions.length && (
              <p className="muted" style={{ margin: 0, fontSize: '.85rem' }}>
                Nothing on this day.
              </p>
            )}

            {detail?.sessions.map((s) => (
              <div key={s.id} style={{ fontSize: '.88rem' }}>
                <strong>{s.title || 'Workout'}</strong>{' '}
                <span className="muted">
                  · {s.set_count} set{s.set_count === 1 ? '' : 's'}
                  {!s.finished_at && ' · in progress'}
                </span>
              </div>
            ))}

            {detail?.planned.map((p) => (
              <div key={p.id} style={{ marginTop: '.2rem' }}>
                <div className="row" style={{ fontSize: '.88rem' }}>
                  <span>
                    {/* A reserved day is literally called "Planned Session",
                        so the usual status prefix would read "Planned ·
                        Planned Session". The status is still carried by the
                        square's colour and the legend; dropping the word here
                        costs nothing and stops the stutter. */}
                    <span className="muted">
                      {p.status === 'completed' ? 'Done · '
                        : p.status === 'skipped' ? 'Missed · '
                          : p.plan_day_name === RESERVED_TITLE ? ''
                            : 'Planned · '}
                    </span>
                    {p.plan_day_name ?? 'Workout'}
                    {/* Which plan put it here. A square used to be anonymous,
                        so with more than one plan going there was no way to
                        tell which one to open to change it — and clearing the
                        wrong plan's schedule looks exactly like clearing being
                        broken. Suppressed when it would just repeat the day
                        name, which is every one-off. */}
                    {p.plan_name && p.plan_name !== p.plan_day_name && (
                      <div className="muted" style={{ fontSize: '.75rem' }}>
                        {p.plan_name}
                      </div>
                    )}
                  </span>
                  {p.status !== 'completed' && onLogDay && (
                    <button
                      className="secondary"
                      style={smallButton}
                      disabled={busy}
                      onClick={() => onLogDay(selected, p)}
                    >
                      {selected < today ? 'Log it' : 'Start'}
                    </button>
                  )}
                </div>

                {/* Editing lives on its own line, under the workout it edits
                    and quieter than starting it. Three buttons of equal weight
                    on one row is how you tap "remove" while reaching for
                    "start" — and on this row that costs a session.
                    Only `planned` rows: the server refuses to rewrite a day
                    that's already been settled, so offering it would be a
                    button that exists to fail. */}
                {p.status === 'planned' && (onRemovePlanned || onMovePlanned) && (
                  <div style={{ display: 'flex', gap: '.6rem', marginTop: '.15rem' }}>
                    {onMovePlanned && (
                      <button
                        className="linky"
                        disabled={busy}
                        onClick={() => { setMoving(p); setError(null) }}
                      >
                        Move
                      </button>
                    )}
                    {onRemovePlanned && (
                      <button
                        className="linky"
                        disabled={busy}
                        onClick={() => void run(async () => {
                          await onRemovePlanned(p)
                          setRemoved(p)
                        })}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Every day, past present or future, whatever else is on it. What
                was scheduled and what you actually want to record are separate
                questions, and any day you can point at is a day you might need
                to answer the second one. A future date is allowed on purpose:
                the calendar is the natural place to put a session down for a
                day that hasn't happened, and refusing would mean the one
                gesture the grid affords stops working halfway across it. */}
            {/* Wrapping, because a future day can offer three things at once
                and three small buttons plus a label don't fit across a phone.
                They drop to the next line rather than squeezing below a
                thumb's width. */}
            <div className="row" style={{ marginTop: '.45rem', flexWrap: 'wrap',
                                          gap: '.4rem' }}>
              <span className="muted" style={{ fontSize: '.78rem' }}>
                {detail?.planned.length ? 'Something else?' : 'Not from a plan'}
              </span>
              <span style={{ display: 'flex', gap: '.35rem', flexWrap: 'wrap' }}>
                {/* One tap, no form: the day is spoken for and that's the
                    whole statement. Reserving a day and writing out what
                    you'll do on it are different intentions that happen at
                    different times — usually days apart — and routing the
                    first through a form that asks about the second is what
                    made putting a square down feel like a chore.
                    Hidden once the day is already reserved: tapping it twice
                    would silently give you two identical squares. */}
                {onReserveDay && selected >= today && !isReserved && (
                  <button
                    className="secondary"
                    style={smallButton}
                    disabled={busy}
                    onClick={() => void run(() => onReserveDay(selected))}
                  >
                    Reserve day
                  </button>
                )}
                {/* Two different questions, and which one a day can answer
                    depends on whether it has happened. A future day gets a
                    workout put down for it; a past one gets a record of what
                    you did. Today can do both — you might be about to train,
                    or you might have trained this morning.
                    "Plan a workout", not "Plan one": what it opens no longer
                    demands a prescription, and the old label promised more
                    work than the gesture now costs. */}
                {onPlanDay && selected >= today && (
                  <button
                    className="secondary"
                    style={smallButton}
                    disabled={busy}
                    onClick={() => onPlanDay(selected)}
                  >
                    Plan a workout
                  </button>
                )}
                {onLogDay && selected <= today && (
                  <button
                    className="secondary"
                    style={smallButton}
                    onClick={() => onLogDay(selected, null)}
                  >
                    {detail?.sessions.length
                      ? 'Add another'
                      : selected === today ? 'Start one' : 'Add workout'}
                  </button>
                )}
              </span>
            </div>
          </>
        )}

        {/* Undo and errors sit outside the day detail on purpose: both are
            about something you just did, not about the day you happen to be
            looking at now, and a removal you can only undo while standing on
            the same square is one most people would never find again.
            No timeout either — an undo that expires while you're deciding is
            the failure mode this exists to prevent. */}
        {removed && onRestorePlanned && (
          <div className="row" style={{ marginTop: '.5rem' }}>
            <span className="muted" style={{ fontSize: '.78rem' }}>
              Removed {removed.plan_day_name ?? 'workout'} from{' '}
              {formatDay(removed.scheduled_date)}.
            </span>
            <button
              className="linky"
              disabled={busy}
              onClick={() => void run(async () => {
                await onRestorePlanned(removed)
                setRemoved(null)
              })}
            >
              Undo
            </button>
          </div>
        )}

        {error && (
          <div className="error" style={{ marginTop: '.5rem', fontSize: '.82rem' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  )
}

const smallButton = {
  marginTop: 0,
  padding: '.2rem .5rem',
  minHeight: 0,
  fontSize: '.78rem',
} as const

const navStyle = {
  marginTop: 0,
  padding: '.15rem .6rem',
  minHeight: 0,
  fontSize: '1rem',
} as const

